import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, GRADE_BADGE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Gavel } from "lucide-react";

const STATUS_BADGES = {
  open: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800",
  awarded: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-300 dark:border-blue-800",
  closed: "bg-muted text-muted-foreground border border-border",
};

export default function Originations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/loan-requests", { params: filter ? { status: filter } : {} })
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <div data-testid="page-originations" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent">Origination Marketplace</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Fund a Loan from Scratch</h1>
        <p className="text-muted-foreground mt-3 max-w-3xl">
          Bid on the terms you'd be willing to fund. Set your own rate (up to 28% APR), term, and payment frequency.
          If the admin accepts your bid, the loan is originated and the note is assigned to you instantly.
        </p>
      </div>

      <div className="flex gap-2 mb-6 border-b border-border pb-4" data-testid="originations-filter">
        {[
          { v: "open", l: "Open" },
          { v: "awarded", l: "Awarded" },
          { v: "closed", l: "Closed" },
          { v: "", l: "All" },
        ].map((o) => (
          <button key={o.v || "all"} data-testid={`filter-${o.v || "all"}`}
            onClick={() => setFilter(o.v)}
            className={`px-4 py-2 text-xs uppercase tracking-wider border ${filter === o.v ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:bg-muted"}`}>
            {o.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground font-mono-data">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-border p-12 text-center text-muted-foreground" data-testid="originations-empty">
          No loan requests in this status.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="originations-grid">
          {items.map((r) => <RequestCard key={r.request_id} request={r} />)}
        </div>
      )}
    </div>
  );
}

function RequestCard({ request }) {
  const { vehicle: v, buyer_profile: b } = request;
  const ltv = request.total_amount ? ((request.loan_amount / request.total_amount) * 100).toFixed(0) : null;
  return (
    <div className="border border-border bg-card hover-lift flex flex-col" data-testid={`request-card-${request.request_id}`}>
      <Link to={`/originations/${request.request_id}`} className="block aspect-[16/10] overflow-hidden bg-muted">
        <img src={v.photo} alt={`${v.make} ${v.model}`} className="w-full h-full object-cover" loading="lazy" />
      </Link>
      <div className="p-5 flex flex-col gap-4 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-overline text-muted-foreground">{v.year} · {b.state}</div>
            <h3 className="font-display text-xl leading-tight mt-1">{v.make} {v.model}</h3>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider ${GRADE_BADGE[b.credit_tier]}`}>
              Tier {b.credit_tier}
            </span>
            <span className={`px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider ${STATUS_BADGES[request.status]}`}>
              {request.status}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-2 border-t border-border pt-4">
          <Metric label="Loan Amount" value={fmtMoney(request.loan_amount)} highlight />
          <Metric label="Total Contract" value={fmtMoney(request.total_amount)} />
          <Metric label="Income (mo)" value={b.monthly_income_range || "—"} />
          <Metric label="DTI" value={b.dti_ratio ? `${b.dti_ratio}%` : "—"} />
          <Metric label="LTV" value={ltv ? `${ltv}%` : "—"} />
          <Metric label="Bids" value={request.bid_count || 0} />
        </div>

        <div className="grid grid-cols-2 gap-2 mt-auto pt-2">
          <Button asChild variant="outline" className="rounded-none uppercase text-xs tracking-wider">
            <Link to={`/originations/${request.request_id}`} data-testid={`view-request-${request.request_id}`}>Details</Link>
          </Button>
          <Button asChild className="rounded-none uppercase text-xs tracking-wider" disabled={request.status !== "open"}>
            <Link to={`/originations/${request.request_id}#bid`} data-testid={`bid-request-${request.request_id}`}>
              <Gavel className="w-3.5 h-3.5 mr-1" />Place Bid
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <div>
      <div className="font-overline text-muted-foreground">{label}</div>
      <div className={`font-mono-data text-base ${highlight ? "text-primary font-semibold" : ""}`}>{value}</div>
    </div>
  );
}
